import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { fadeUp, staggerChildren } from "../lib/motion";

/* -------------------------------------------------------------------------- */
/* Page header                                                                 */
/* -------------------------------------------------------------------------- */

export interface Breadcrumb {
  label: string;
  href?: string;
}

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  breadcrumbs?: Breadcrumb[];
  actions?: ReactNode;
  /** Rendered under the header, above the gold rule — filters usually. */
  children?: ReactNode;
  /** Router link component, so this package stays router-agnostic. */
  linkComponent?: (props: { href: string; children: ReactNode }) => ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  icon: Icon,
  breadcrumbs,
  actions,
  children,
  linkComponent,
  className,
}: PageHeaderProps) {
  return (
    <motion.header
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className={cn("mb-6", className)}
    >
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="mb-2">
          <ol className="flex flex-wrap items-center gap-1 text-xs text-content-subtle">
            {breadcrumbs.map((crumb, index) => (
              <li key={`${crumb.label}-${index}`} className="flex items-center gap-1">
                {index > 0 && (
                  <ChevronRight size={12} aria-hidden="true" className="opacity-60" />
                )}
                {crumb.href && linkComponent ? (
                  <span className="transition-colors hover:text-gold-600">
                    {linkComponent({ href: crumb.href, children: crumb.label })}
                  </span>
                ) : (
                  <span
                    className={cn(
                      index === breadcrumbs.length - 1 && "text-content-muted",
                    )}
                  >
                    {crumb.label}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {Icon && (
            <span className="mt-1 grid size-10 shrink-0 place-items-center rounded-xl border border-gold-300/50 bg-gold-100 text-gold-700 dark:border-gold-800 dark:bg-gold-950/60 dark:text-gold-400">
              <Icon size={19} aria-hidden="true" />
            </span>
          )}
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-content sm:text-2xl">
              {title}
            </h1>
            {description && (
              <p className="mt-1 max-w-2xl text-sm text-content-muted">{description}</p>
            )}
          </div>
        </div>

        {actions && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>

      <span aria-hidden="true" className="mt-4 block h-px w-full rule-gold" />

      {children && <div className="mt-4">{children}</div>}
    </motion.header>
  );
}

/* -------------------------------------------------------------------------- */
/* Stat tiles                                                                  */
/* -------------------------------------------------------------------------- */

export interface StatCardProps {
  label: string;
  value: ReactNode;
  icon?: LucideIcon;
  hint?: ReactNode;
  /** Direction of change, if the metric has a comparison. */
  trend?: { value: string; direction: "up" | "down" | "flat" };
  loading?: boolean;
  className?: string;
}

export function StatCard({
  label,
  value,
  icon: Icon,
  hint,
  trend,
  loading = false,
  className,
}: StatCardProps) {
  return (
    <motion.div
      variants={fadeUp}
      className={cn(
        "group relative overflow-hidden rounded-xl border border-line bg-surface p-4 shadow-ir-sm",
        "transition-all duration-300 hover:border-gold-300 hover:shadow-ir-md dark:hover:border-gold-800",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="absolute -right-8 -top-8 size-24 rounded-full bg-gold-400/10 blur-2xl transition-opacity duration-500 group-hover:opacity-150"
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium uppercase tracking-[0.07em] text-content-subtle">
            {label}
          </p>
          {loading ? (
            <span className="mt-2 block h-8 w-20 animate-shimmer rounded bg-[linear-gradient(90deg,var(--ir-surface-sunken)_25%,var(--ir-line)_37%,var(--ir-surface-sunken)_63%)] bg-[length:200%_100%]" />
          ) : (
            <p className="mt-1.5 text-2xl font-semibold tabular text-content">{value}</p>
          )}
          {(hint || trend) && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-content-subtle">
              {trend && (
                <span
                  className={cn(
                    "font-medium",
                    trend.direction === "up" && "text-success-600 dark:text-success-500",
                    trend.direction === "down" && "text-danger-600 dark:text-danger-500",
                  )}
                >
                  {trend.value}
                </span>
              )}
              {hint}
            </p>
          )}
        </div>
        {Icon && (
          <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-gold-100 text-gold-700 dark:bg-gold-950/60 dark:text-gold-400">
            <Icon size={17} aria-hidden="true" />
          </span>
        )}
      </div>
    </motion.div>
  );
}

export function StatGrid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div
      variants={staggerChildren(0.06)}
      initial="hidden"
      animate="visible"
      className={cn("grid gap-3 sm:grid-cols-2 xl:grid-cols-4", className)}
    >
      {children}
    </motion.div>
  );
}

/* -------------------------------------------------------------------------- */
/* Description lists                                                           */
/* -------------------------------------------------------------------------- */

export interface DetailItem {
  label: ReactNode;
  value: ReactNode;
  /** Drop rows whose value is empty rather than printing a dash everywhere. */
  hideWhenEmpty?: boolean;
  span?: 1 | 2;
}

function isEmptyValue(value: ReactNode): boolean {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    value === "—" ||
    (Array.isArray(value) && value.length === 0)
  );
}

export function DetailList({
  items,
  columns = 2,
  layout = "inline",
  className,
}: {
  items: DetailItem[];
  columns?: 1 | 2 | 3;
  /**
   * `inline` puts the label beside the value, which reads well for short
   * values. `stacked` puts it above and hands the value the full column — use
   * it where the values are long identifiers. A 36-character UUID needs about
   * 273px; beside an 11rem label in a two-column grid it gets 245px and wraps,
   * and trimming the label alone does not close that gap.
   *
   * Stacked is simply the layout every row already uses below `sm`, kept at
   * every width.
   */
  layout?: "inline" | "stacked";
  className?: string;
}) {
  const visible = items.filter(
    (item) => !(item.hideWhenEmpty && isEmptyValue(item.value)),
  );

  if (visible.length === 0) return null;

  // A spanning item must never span more columns than the grid actually has.
  // `col-span-3` inside `grid-cols-2` makes the browser add an implicit third
  // column, which steals width from the two real ones — the symptom is a value
  // column collapsing to ~30px and dates wrapping as "Sep t 26, 22: 57".
  const spanClass =
    columns === 1
      ? ""
      : columns === 2
        ? "sm:col-span-2"
        : "sm:col-span-2 xl:col-span-3";

  return (
    <dl
      className={cn(
        "grid gap-x-6 gap-y-0",
        columns === 1 && "grid-cols-1",
        columns === 2 && "sm:grid-cols-2",
        columns === 3 && "sm:grid-cols-2 xl:grid-cols-3",
        className,
      )}
    >
      {visible.map((item, index) => (
        <div
          key={index}
          className={cn(
            "flex flex-col gap-0.5 border-b border-line/70 py-2.5 last:border-0",
            layout === "inline" && "sm:flex-row sm:items-baseline sm:gap-4",
            item.span === 2 && spanClass,
          )}
        >
          <dt
            className={cn(
              "shrink-0 text-xs font-medium uppercase tracking-wide text-content-subtle",
              layout === "inline" && "sm:w-44",
            )}
          >
            {item.label}
          </dt>
          <dd className="min-w-0 flex-1 break-words text-sm text-content">
            {isEmptyValue(item.value) ? (
              <span className="text-content-subtle">—</span>
            ) : (
              item.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* -------------------------------------------------------------------------- */
/* Toolbar                                                                     */
/* -------------------------------------------------------------------------- */

export function Toolbar({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-xl border border-line bg-surface p-3 sm:flex-row sm:items-center",
        className,
      )}
    >
      {children}
    </div>
  );
}
